/**
 * G5 hooks: the learned tendencies (derived from every machine's encrypted report and the sealed
 * inventories, saved to the sealed `tendencies` setting when they change) and the device-side
 * deps for scoping and accepting a setup.
 */
import { useQuery } from '@tanstack/react-query'
import { ProjectInventory } from '@viko/protocol'
import { useMemo } from 'react'
import type { Backend } from '../../lib/backend.js'
import { useBackend } from '../../lib/backend.js'
import type { BarkStore } from '../../lib/bark-store.js'
import { loadGoal, saveGoal } from '../../lib/goals.js'
import type { AcceptDeps, ScopingDeps } from '../../lib/project-setup.js'
import { deriveTendencies, saveTendencies } from '../../lib/tendencies.js'
import { useCheckoutDeps } from '../NewProjectDialog.js'

export async function loadTendencySources(b: Backend) {
  const reports = await b.api.machineProjects()
  const ents = b.entities
  const inventories = ents
    ? (await ents.list('inventory')).flatMap((r) => {
        const p = ProjectInventory.safeParse(r.value)
        return p.success
          ? [
              {
                projectId: p.data.projectId,
                technologies: p.data.entries
                  .filter((e) => e.category === 'technology')
                  .map((e) => e.name.toLowerCase()),
              },
            ]
          : []
      })
    : []
  return { reports, inventories }
}

export function useTendencies() {
  const b = useBackend()
  return useQuery({
    queryKey: ['tendencies', b.artifactAccess.accountId, !!b.entities] as const,
    queryFn: async () => {
      const t = deriveTendencies(await loadTendencySources(b))
      // Keep the sealed setting current for other devices; never block the UI on it.
      if (b.entities) await saveTendencies(b.entities, t).catch(() => false)
      return t
    },
    staleTime: 5 * 60_000,
    retry: false,
  })
}

export function scopingDeps(b: Backend, store: BarkStore | undefined): ScopingDeps {
  return {
    createAccountProject: (body) => b.api.createAccountProject(body),
    updateSetup: (projectId, setup) => b.api.updateProject(projectId, { setup }),
    dispatchScope: (machineId, payload) => b.dispatch('project.scope', machineId, payload),
    submitBark: async (i) => {
      if (!store) throw new Error('Unlock this browser to talk with the agent.')
      return store.submit({ ...i, mode: 'discuss', source: 'tab' })
    },
    now: () => Date.now(),
  }
}

export function useAcceptDeps(): AcceptDeps {
  const b = useBackend()
  const checkout = useCheckoutDeps()
  return useMemo(
    () => ({
      ...checkout,
      recordCheckouts: (id, body) => b.api.recordCheckouts(id, body),
      saveGoal: async (projectId, goal) => {
        const ents = b.entities
        if (!ents) throw new Error('Unlock this browser to save the goal.')
        const known = await loadGoal(ents, projectId)
        return saveGoal(
          ents,
          projectId,
          { text: goal.text, status: 'active', milestones: goal.milestones },
          known,
        )
      },
      createWorkstream: (i) => b.api.createWorkstream(i),
      createTask: (i) => b.api.createTask(i),
      updateSetup: (projectId, setup) => b.api.updateProject(projectId, { setup }),
    }),
    [b, checkout],
  )
}
